import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/slice';

// PACKAGES
import ScrollContainer from 'react-indiana-drag-scroll';
import Moment from 'moment';
import { Spinner } from 'react-bootstrap';

// COMPONENTS
import StatusCell from './StatusCell';
import ScrollButtons from './ScrollButtons';
import SortButton from './SortButton';
import PaginationCustom from '../../Pagination/Pagination';

// UTILS
import SvgMask from '../../_Helpers/SvgMask';
import { guid } from '../../../libs/utils';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';

// TYPES
import apibridge, { GetAssessmentOverviewTypes } from '../../../apibridge';
import { AssessmentSummaryReadingReadingInfo } from '../../../api/models';

interface ReadingTableProps {
	assessmentReadingTableParams: GetAssessmentOverviewTypes['ReadingType'];
	setAssessmentReadingTableParams: React.Dispatch<React.SetStateAction<GetAssessmentOverviewTypes['ReadingType']>>;
	onStudentClick: (classId: string, studentId: string) => Promise<void>;
}

const ReadingTable: FC<ReadingTableProps> = ({
	assessmentReadingTableParams,
	setAssessmentReadingTableParams,
	onStudentClick
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);
	const [readingData, setReadingData] = useState<AssessmentSummaryReadingReadingInfo>();

	const {
		currentPage = 0,
		itemsPerPage = 0,
		stages = [],
		students = [],
		totalItems = 0,
		totalPages = 0
	} = readingData || {};

	// Scroll Logic
	const [scrollPosition, setScrollPosition] = useState(0);
	const [scrollWidth, setScrollWidth] = useState(0);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const getReadingData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentSummaryReading(assessmentReadingTableParams);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setReadingData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Reading overview error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		if (!isLoading && scrollContainerRef.current) {
			const containerWidth = scrollContainerRef.current.clientWidth;
			const contentWidth = scrollContainerRef.current.scrollWidth;
			const maxScroll = contentWidth - containerWidth;
			setScrollWidth(maxScroll);
		}
	}, [isLoading]);

	useEffect(() => {
		getReadingData();
	}, [assessmentReadingTableParams]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset page to 1 when params change, otherwise pagination will break
	useEffect(() => {
		if ((assessmentReadingTableParams.pageNumber || 1) !== 1) {
			setAssessmentReadingTableParams({ ...assessmentReadingTableParams, pageNumber: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		assessmentReadingTableParams._class,
		assessmentReadingTableParams.assessmentPeriodId,
		assessmentReadingTableParams.benchmark
	]);

	return (
		<div className="assessment-general-table">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<div className={`table-container text-shades-800 ${isReloading ? 'opacity-75' : ''}`}>
						{/* STUDENT DETAILS COLUMN */}
						<div className="table-col-students">
							{/* Student Name List Header */}
							<div className="student-row-heading border-bottom-0"></div>

							<div className="student-row-heading">
								<div className="student-name-cell d-flex align-items-center justify-content-between gap-3">
									<span className="body-tiny">
										<strong>Student name</strong>
									</span>

									<SortButton
										type="Student"
										params={assessmentReadingTableParams}
										setParams={setAssessmentReadingTableParams}
										disabled={isReloading}
									/>
								</div>

								<div className="last-recorded-cell">
									<span className="body-tiny">
										<strong>Last recorded</strong>
									</span>

									<SortButton
										type="LastRecorded"
										params={assessmentReadingTableParams}
										setParams={setAssessmentReadingTableParams}
										disabled={isReloading}
									/>
								</div>
							</div>

							{/* Student name List */}
							{students.length > 0 ? (
								<div className="student-buttons-wrapper">
									{students.map((student, studentIndex) => {
										return (
											<button
												className={`btn btn-student student-row d-flex justify-content-between ${
													studentIndex % 2 === 0 ? 'bg-white' : ''
												}`}
												key={studentIndex}
												onClick={() => onStudentClick(student.classId || '', student.id || '')}
											>
												<div className="student-name-cell d-flex flex-column justify-content-center">
													<span className="student-name body-small">
														<strong>
															{student.firstName} {student.lastName}
														</strong>
													</span>

													<span className="body-mini fw-semibold opacity-50">{student.className}</span>
												</div>

												<div className="last-recorded-cell">
													<span className="body-tiny fw-semibold">
														{Moment(student.lastRecorded).format('DD/MM/YY')}
													</span>

													<div className="edit-btn">
														<SvgMask path="/svg/edit.svg" width={24} height={24} />
													</div>
												</div>
											</button>
										);
									})}
								</div>
							) : (
								<div className="student-row d-flex justify-content-between">
									<div className="student-name-cell d-flex flex-column justify-content-center">
										<p className="my-3">No results</p>
									</div>
								</div>
							)}
						</div>

						{/* SCROLL CONTAINER */}
						<div className="table-col-content d-flex scroll-btn-anchor">
							<ScrollButtons
								scrollPosition={scrollPosition}
								setScrollPosition={setScrollPosition}
								scrollWidth={scrollWidth}
								scrollContainerRef={scrollContainerRef}
							/>
							<ScrollContainer
								className="stages-scroll-container"
								hideScrollbars={true}
								vertical={false}
								innerRef={scrollContainerRef}
								onScroll={() => {
									if (scrollContainerRef.current) {
										setScrollPosition(scrollContainerRef.current.scrollLeft);
									}
								}}
							>
								{/* Header */}
								<div className="d-flex">
									{stages.length > 0 &&
										stages.map((stage, stageIndex) => {
											const { id, name = '', graphemes = [] } = stage;
											const stageClassName = name.replace('.', '-');

											const isMediumCell = stageClassName.startsWith('7');

											return (
												<div key={stageIndex} className="stage-col">
													{/* Stage Name */}
													<div className={`stage-name stage-${stageClassName}`}>Stage {name}</div>

													{/* Stage Progress Name */}
													<div className="d-flex">
														{graphemes.map((grapheme, graphemeIndex) => {
															const isSentenceGrapheme = grapheme?.text?.includes(' ');

															const numberOfSpaceCharacters = (grapheme?.text?.match(/ /g) || []).length;

															const graphemeLength = grapheme?.text?.length || 0;

															const multiplier = numberOfSpaceCharacters > 0 && numberOfSpaceCharacters < 3 ? 12 : 9;

															const graphemeWidth =
																isSentenceGrapheme && graphemeLength > 0 ? graphemeLength * multiplier : 0;

															return (
																<div
																	className={`stage-progress ${
																		graphemeWidth ? 'no-max-width' : isMediumCell ? 'medium' : 'small'
																	} stage-${stageClassName}`}
																	key={graphemeIndex}
																	// Only apply the styling when the grapheme is a sentence
																	{...(graphemeWidth ? { style: { width: `${graphemeWidth}px` } } : {})}
																>
																	<span className="stage-progress-name">{grapheme.text}</span>

																	<SortButton
																		type="Text"
																		params={assessmentReadingTableParams}
																		setParams={setAssessmentReadingTableParams}
																		stageId={id}
																		sortText={grapheme.text}
																		disabled={isReloading}
																	/>
																</div>
															);
														})}
													</div>
												</div>
											);
										})}
								</div>

								{/* Student Cells */}
								{students.length > 0 && (
									<div className="table-row-wrapper">
										{students.map((student, studentIndex) => {
											return (
												<div key={studentIndex} className="table-row d-flex">
													{stages.map((stage, stageIndex) => {
														const { id, name = '', graphemes = [] } = stage;

														const isMediumCell = name.startsWith('7');

														return graphemes.map((grapheme, graphemeIndex) => {
															const studentGraphemeData = student.graphemePhonemes?.find(
																(graphemePhoneme) =>
																	graphemePhoneme.stageId === id && graphemePhoneme.text === grapheme.text
															);

															// Custom width for sentence graphemes
															const isSentenceGrapheme = grapheme?.text?.includes(' ');

															const numberOfSpaceCharacters = (grapheme?.text?.match(/ /g) || []).length;

															const multiplier = numberOfSpaceCharacters > 0 && numberOfSpaceCharacters < 3 ? 12 : 9;

															const sentenceGraphemeWidth =
																isSentenceGrapheme && (grapheme?.text?.length || 0) > 0
																	? (grapheme?.text?.length || 0) * multiplier
																	: 0;

															return (
																<StatusCell
																	key={`${stageIndex}-${graphemeIndex}`}
																	className={`${studentIndex % 2 === 0 ? 'alternate-bg-white' : ''}`}
																	width={isMediumCell ? 'medium' : 'small'}
																	customWidth={sentenceGraphemeWidth}
																	type="binary-result"
																	isCorrect={
																		studentGraphemeData?.status === undefined ||
																		studentGraphemeData?.status === 'Pending'
																			? undefined
																			: studentGraphemeData?.status === 'Successful'
																	}
																/>
															);
														});
													})}
												</div>
											);
										})}
									</div>
								)}
							</ScrollContainer>
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-5 d-flex justify-content-between align-items-center">
							<PaginationCustom
								pageNumber={assessmentReadingTableParams.pageNumber || 1}
								totalPages={totalPages}
								pageSwitchCallback={(newPageNumber) => {
									setAssessmentReadingTableParams({
										...assessmentReadingTableParams,
										pageNumber: newPageNumber
									});
								}}
							/>

							<div className="body-small">
								<strong>
									Showing {itemsPerPage * (currentPage - 1) + 1} -{' '}
									{currentPage < totalPages ? itemsPerPage * currentPage : totalItems} of {totalItems}{' '}
									{totalItems === 1 ? 'student' : 'students'}
								</strong>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default ReadingTable;
